import { useState, useRef, useEffect } from 'react';
import styles from './CustomSelect.module.css';

interface BaseCustomSelectProps {
	options: Array<{ value: string; label: string }>;
	placeholder?: string;
	disabled?: boolean;
	error?: string;
	required?: boolean;
	className?: string;
	searchable?: boolean;
}

interface SingleCustomSelectProps extends BaseCustomSelectProps {
	value: string;
	onChange: (value: string) => void;
	multiple?: false;
}

interface MultipleCustomSelectProps extends BaseCustomSelectProps {
	value: string[];
	onChange: (value: string[]) => void;
	multiple: true;
}

type CustomSelectProps = SingleCustomSelectProps | MultipleCustomSelectProps;

const SelectArrow = () => (
	<svg
		className={styles.arrow}
		width="11"
		height="7"
		viewBox="0 0 12 8"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M1 1.5L6 6.5L11 1.5"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export const CustomSelect = ({
	label,
	value,
	onChange,
	options,
	placeholder = 'Selecione...',
	disabled = false,
	error,
	required = false,
	className = '',
	searchable = false,
	multiple = false,
}: CustomSelectProps & { label?: string }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);

	const selectedValues = multiple ? (value as string[]) : [];
	const selectedOption = !multiple
		? options.find((opt) => opt.value === (value as string))
		: undefined;

	const displayText = multiple
		? selectedValues.length === 0
			? placeholder
			: selectedValues.length <= 2
				? selectedValues
						.map((v) => options.find((o) => o.value === v)?.label ?? v)
						.join(', ')
				: `${selectedValues.length} selecionados`
		: (selectedOption?.label ?? placeholder);

	const hasValue = multiple ? selectedValues.length > 0 : !!(value as string);

	const visibleOptions = searchable && search.trim()
		? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
		: options;

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setSearch('');
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			if (searchable) {
				setTimeout(() => searchRef.current?.focus(), 0);
			}
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, searchable]);

	const handleToggle = () => {
		if (!disabled) {
			setIsOpen((prev) => !prev);
			if (isOpen) setSearch('');
		}
	};

	const handleSelect = (optionValue: string) => {
		if (multiple) {
			const current = value as string[];
			const next = current.includes(optionValue)
				? current.filter((v) => v !== optionValue)
				: [...current, optionValue];
			(onChange as (v: string[]) => void)(next);
		} else {
			(onChange as (v: string) => void)(optionValue);
			setIsOpen(false);
			setSearch('');
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (disabled) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setIsOpen((prev) => !prev);
		} else if (e.key === 'Escape') {
			setIsOpen(false);
			setSearch('');
		}
	};

	return (
		<div className={`${styles.container} ${className}`} ref={containerRef}>
			{label && (
				<label className={styles.label}>
					{label}
					{required && <span className={styles.required}>*</span>}
				</label>
			)}
			<div
				className={`${styles.selectWrapper} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''} ${isOpen ? styles.open : ''}`}
				onClick={handleToggle}
				onKeyDown={handleKeyDown}
				tabIndex={disabled ? -1 : 0}
				role="combobox"
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label={label}
				aria-required={required}
				aria-invalid={!!error}
			>
				<span className={`${styles.selectedValue} ${!hasValue ? styles.placeholder : ''}`}>
					{displayText}
				</span>
				<SelectArrow />
			</div>

			{isOpen && (
				<div className={styles.dropdown} role="listbox">
					{searchable && (
						<div className={styles.searchWrapper}>
							<input
								ref={searchRef}
								type="text"
								className={styles.searchInput}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Pesquisar..."
								onMouseDown={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							/>
						</div>
					)}
					<div className={styles.optionsList}>
						{visibleOptions.length === 0 ? (
							<div className={styles.noResults}>Nenhum resultado</div>
						) : (
							visibleOptions.map((option) => {
								const isSelected = multiple
									? selectedValues.includes(option.value)
									: option.value === (value as string);
								return (
									<div
										key={option.value}
										className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
										onClick={() => handleSelect(option.value)}
										role="option"
										aria-selected={isSelected}
									>
										{multiple && (
											<input
												type="checkbox"
												readOnly
												checked={isSelected}
												tabIndex={-1}
												className={styles.checkMark}
											/>
										)}
										{option.label}
									</div>
								);
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
};
